import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Rocket, ChevronDown, ChevronUp, RefreshCw, CheckCircle2, AlertCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthToken } from '../../utils/auth';
import SimpleVenture from './SimpleVenture';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

const STATUS_STYLE = {
  queued: 'text-zinc-400 border-zinc-500/30',
  running: 'text-blue-300 border-blue-400/30',
  ready: 'text-emerald-300 border-emerald-400/30',
  building: 'text-amber-300 border-amber-400/30',
  quoting: 'text-amber-300 border-amber-400/30',
  done: 'text-emerald-300 border-emerald-400/30',
  parked: 'text-zinc-500 border-zinc-600/30',
  failed: 'text-red-300 border-red-400/30',
};

const VerifyBadge = ({ verification }) => (
  <span
    className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${
      verification === 'verified'
        ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
        : 'text-amber-300/80 border-amber-400/30 bg-amber-500/5'
    }`}
  >
    {verification === 'verified' ? 'verified' : 'unverified'}
  </span>
);

const QuoteForm = ({ projectId, partId, onLogged }) => {
  const [vendor, setVendor] = useState('');
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!vendor || !price) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/idea/projects/${projectId}/quote`,
        { part_id: partId, vendor_name: vendor, unit_price: parseFloat(price), source: 'user' },
        auth()
      );
      toast.success('Verified quote logged');
      onLogged();
    } catch (e) {
      toast.error('Failed to log quote');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5 mt-1">
      <input
        data-testid={`quote-vendor-${partId}`}
        value={vendor}
        onChange={(e) => setVendor(e.target.value)}
        placeholder="vendor"
        className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-[11px] w-28 text-zinc-200"
      />
      <input
        data-testid={`quote-price-${partId}`}
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        placeholder="$/unit"
        type="number"
        className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-[11px] w-20 text-zinc-200"
      />
      <button
        data-testid={`quote-save-${partId}`}
        onClick={submit}
        disabled={saving}
        className="text-[11px] text-emerald-300 hover:text-emerald-200 font-mono uppercase px-1.5"
      >
        {saving ? '...' : 'log'}
      </button>
    </div>
  );
};

const VersionTimeline = ({ projectId }) => {
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    axios
      .get(`${API_URL}/api/idea/projects/${projectId}/versions`, auth())
      .then((r) => setVersions(r.data.versions || []))
      .catch(() => {});
  }, [projectId]);
  if (versions.length < 2) return null;
  return (
    <div data-testid="idea-versions">
      <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5">
        Design evolution · {versions.length} versions
      </div>
      <div className="space-y-1">
        {versions.map((v, i) => (
          <div key={i} className="text-[12px] text-zinc-400 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-zinc-300">v{v.version}{v.current ? ' (current)' : ''}</span>
            {v.est_cost?.est_total_high != null && (
              <span>est ${v.est_cost.est_total_low?.toLocaleString()}–${v.est_cost.est_total_high?.toLocaleString()}</span>
            )}
            {v.cost_delta_vs_previous != null && (
              <span className={v.cost_delta_vs_previous > 0 ? 'text-red-300/80' : 'text-emerald-300/80'}>
                {v.cost_delta_vs_previous > 0 ? '+' : ''}${v.cost_delta_vs_previous.toLocaleString()} vs prev
              </span>
            )}
            {v.change_that_followed && (
              <span className="text-zinc-600">→ then: {v.change_that_followed.slice(0, 60)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ProjectDetail = ({ projectId, refreshKey }) => {
  const [viewMode, setViewMode] = useState('simple');
  const [detail, setDetail] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [quotingPart, setQuotingPart] = useState(null);
  const [dkChecking, setDkChecking] = useState(false);
  const [gateFail, setGateFail] = useState(null);

  const checkDigikey = async () => {
    setDkChecking(true);
    try {
      const r = await axios.post(`${API_URL}/api/idea/projects/${projectId}/digikey/check`, {}, auth());
      toast.success(`Digi-Key: live matches for ${r.data.matched} of ${r.data.checked} unverified items`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Digi-Key check failed');
    } finally {
      setDkChecking(false);
    }
  };

  const confirmDigikey = async (partId, dkpn, override = false) => {
    try {
      await axios.post(
        `${API_URL}/api/idea/projects/${projectId}/digikey/confirm`,
        { part_id: partId, digikey_product_number: dkpn, override,
          override_reason: override ? 'owner override via UI' : null },
        auth()
      );
      setGateFail(null);
      toast.success(override ? 'Overridden and verified — override recorded' : 'Verified with live Digi-Key price');
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.gate_failed) {
        setGateFail({ partId, dkpn, ...detail });
        toast.error('Spec gate refused this part — see details on the item');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to confirm match');
      }
    }
  };

  const draftRfq = async (partId) => {
    try {
      await axios.post(`${API_URL}/api/idea/projects/${projectId}/parts/${partId}/rfq`, {}, auth());
      toast.success('RFQ draft created — nothing sends without your approval');
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to draft RFQ');
    }
  };

  const load = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([
        axios.get(`${API_URL}/api/idea/projects/${projectId}`, auth()),
        axios.get(`${API_URL}/api/idea/projects/${projectId}/rfqs`, auth()),
      ]);
      setDetail(d.data);
      setRfqs(r.data.rfqs || []);
    } catch (_e) { /* silent */ }
  }, [projectId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!detail) return <div className="text-zinc-500 text-sm py-3">Loading…</div>;

  const recs = detail.recommendations || {};
  const vendors = detail.vendors || {};

  if (viewMode === 'simple') {
    return (
      <div className="pt-1" data-testid={`idea-detail-${projectId}`}>
        <div className="flex justify-end">
          <button
            data-testid="toggle-advanced-view"
            onClick={() => setViewMode('advanced')}
            className="text-[10px] font-mono uppercase text-zinc-600 hover:text-zinc-400"
          >
            advanced view →
          </button>
        </div>
        <SimpleVenture projectId={projectId} />
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-3" data-testid={`idea-detail-${projectId}`}>
      <div className="flex justify-end">
        <button
          data-testid="toggle-simple-view"
          onClick={() => setViewMode('simple')}
          className="text-[10px] font-mono uppercase text-zinc-600 hover:text-zinc-400"
        >
          ← simple view
        </button>
      </div>
      {detail.summary && (
        <p className="text-[13px] text-zinc-400 leading-relaxed">{detail.summary}</p>
      )}

      {detail.est_cost && (
        <div className="text-[12px] text-zinc-300 font-mono" data-testid="idea-cost-summary">
          Est. total: ${detail.est_cost.est_total_low?.toLocaleString()} – $
          {detail.est_cost.est_total_high?.toLocaleString()}
          <span className="text-amber-300/70 ml-2">
            ({detail.est_cost.verified_parts || 0} verified · rest unverified estimates)
          </span>
        </div>
      )}

      {detail.scorecard && (
        <div
          data-testid="idea-scorecard"
          className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2.5"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span
              className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border ${
                detail.scorecard.readiness === 'ready_to_order'
                  ? 'text-emerald-300 border-emerald-400/40'
                  : detail.scorecard.readiness === 'partially_verified'
                  ? 'text-amber-300 border-amber-400/40'
                  : 'text-red-300/90 border-red-400/30'
              }`}
            >
              {detail.scorecard.readiness.replace(/_/g, ' ')}
            </span>
            <span className="text-[12px] font-mono text-zinc-300">
              {detail.scorecard.verified_items} verified · {detail.scorecard.assumed_items} assumed
              ({detail.scorecard.verified_pct}%)
            </span>
            <span className="text-[11px] text-zinc-500">
              critical: {detail.scorecard.critical_verified}/{detail.scorecard.critical_items} verified
            </span>
          </div>
          {(detail.scorecard.gates || []).length > 0 && (
            <div className="mt-1.5 space-y-0.5" data-testid="idea-scorecard-gates">
              {detail.scorecard.gates.map((g, i) => (
                <div key={i} className="text-[11px] text-red-300/80">⛔ {g}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {detail.review && (
        <div data-testid="idea-review">
          <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5">
            Adversarial review · {(detail.review.flags || []).length} flag(s)
          </div>
          {detail.review.overall_assessment && (
            <p className="text-[12px] text-zinc-400 mb-2">{detail.review.overall_assessment}</p>
          )}
          <div className="space-y-1.5">
            {(detail.review.flags || []).map((f, i) => (
              <div
                key={i}
                data-testid={`review-flag-${i}`}
                className={`rounded border px-2.5 py-1.5 text-[12px] ${
                  f.resolved
                    ? 'border-white/[0.05] text-zinc-600 line-through'
                    : f.severity === 'high'
                    ? 'border-red-400/30 bg-red-500/[0.05] text-zinc-300'
                    : f.severity === 'medium'
                    ? 'border-amber-400/25 bg-amber-500/[0.04] text-zinc-300'
                    : 'border-white/[0.06] text-zinc-400'
                }`}
              >
                <span className="font-mono text-[10px] uppercase mr-2">
                  {f.severity}
                </span>
                {f.issue}
                {f.suggestion && !f.resolved && (
                  <span className="text-zinc-500"> → {f.suggestion}</span>
                )}
                {!f.resolved && (
                  <button
                    data-testid={`resolve-flag-${i}`}
                    onClick={async () => {
                      const resolution = window.prompt('How was this resolved?');
                      if (!resolution) return;
                      try {
                        await axios.post(
                          `${API_URL}/api/idea/projects/${projectId}/flags/resolve`,
                          { flag_index: i, resolution },
                          auth()
                        );
                        toast.success('Flag resolved');
                        load();
                      } catch (_e) { toast.error('Failed to resolve flag'); }
                    }}
                    className="ml-2 text-[10px] text-emerald-300/80 hover:text-emerald-200 font-mono uppercase"
                  >
                    resolve
                  </button>
                )}
              </div>
            ))}
          </div>
          {(detail.review.missing_items || []).length > 0 && (
            <div className="text-[12px] text-amber-300/80 mt-2">
              Reviewer says missing: {detail.review.missing_items.join(' · ')}
            </div>
          )}
          {detail.review.cost_realism && (
            <div className="text-[11px] text-zinc-500 mt-1.5 italic">
              Cost realism: {detail.review.cost_realism}
            </div>
          )}
        </div>
      )}

      {(detail.bom || []).length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">
              Bill of materials · {detail.bom.length} items
            </div>
            <button
              data-testid="digikey-check-btn"
              onClick={checkDigikey}
              disabled={dkChecking}
              className="text-[10px] font-mono uppercase px-2 py-1 rounded border border-red-400/30 text-red-300/90 hover:bg-red-500/10 disabled:opacity-50"
            >
              {dkChecking ? 'checking Digi-Key…' : 'check live Digi-Key prices'}
            </button>
          </div>
          <div className="space-y-2">
            {detail.bom.map((p) => {
              const rec = recs[p.part_id];
              return (
                <div
                  key={p.part_id}
                  data-testid={`bom-item-${p.part_id}`}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[13px] text-zinc-100">
                      {p.name}
                      <span className="text-zinc-500 ml-2 text-[11px]">
                        ×{p.qty} {p.unit}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] text-zinc-300 font-mono">
                        {p.verified_unit_price != null
                          ? `$${p.verified_unit_price.toLocaleString()}/u`
                          : p.est_unit_cost_low != null
                          ? `~$${p.est_unit_cost_low}–${p.est_unit_cost_high ?? p.est_unit_cost_low}/u`
                          : 'no estimate'}
                      </span>
                      <VerifyBadge verification={p.verification} />
                    </div>
                  </div>
                  {p.spec && <div className="text-[11px] text-zinc-500 mt-0.5">{p.spec}</div>}
                  {p.verification !== 'verified' && (p.digikey_candidates || []).length > 0 && (
                    <div className="mt-1.5 space-y-1" data-testid={`dk-candidates-${p.part_id}`}>
                      {p.digikey_candidates.map((c) => (
                        <div
                          key={c.digikey_product_number}
                          className="flex items-center gap-2 text-[11px] text-zinc-400 flex-wrap"
                        >
                          <span className="text-red-300/80 font-mono shrink-0">DK</span>
                          <span className="truncate max-w-[280px]">
                            {c.manufacturer_product_number} — {c.description}
                          </span>
                          <span className="font-mono text-zinc-300">${c.unit_price}/u</span>
                          <span className="text-zinc-600">stock {c.stock?.toLocaleString?.() ?? c.stock}</span>
                          <button
                            data-testid={`dk-confirm-${p.part_id}-${c.digikey_product_number}`}
                            onClick={() => confirmDigikey(p.part_id, c.digikey_product_number)}
                            className="text-[10px] font-mono uppercase text-emerald-300 hover:text-emerald-200"
                          >
                            use this price
                          </button>
                        </div>
                      ))}
                      <div className="text-[10px] text-zinc-600 italic">
                        live Digi-Key prices — confirm the exact match to mark verified, or ignore if none fit
                      </div>
                      {gateFail && gateFail.partId === p.part_id && (
                        <div data-testid={`spec-gate-${p.part_id}`} className="rounded border border-red-400/30 bg-red-500/5 px-2 py-1.5 mt-1 space-y-1">
                          <div className="text-[11px] text-red-300">
                            SPEC GATE REFUSED · {gateFail.reason}
                          </div>
                          {(gateFail.alternatives || []).length > 0 && (
                            <div className="text-[11px] text-zinc-400">
                              Better alternatives:{' '}
                              {gateFail.alternatives.map((a) => `${a.manufacturer_product_number} ($${a.unit_price}/u)`).join(' · ')}
                            </div>
                          )}
                          <button
                            data-testid={`spec-gate-override-${p.part_id}`}
                            onClick={() => confirmDigikey(gateFail.partId, gateFail.dkpn, true)}
                            className="text-[10px] font-mono uppercase text-red-300/80 hover:text-red-200 underline"
                          >
                            override anyway (permanently recorded)
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {p.verification !== 'verified' && p.digikey_checked_at && (p.digikey_candidates || []).length === 0 && (
                    <div className="text-[10px] text-zinc-600 mt-1">no Digi-Key match — likely not a distributor electronics item</div>
                  )}
                  {rec && (
                    <div className="text-[12px] text-zinc-400 mt-1">
                      <span className="text-emerald-300/90">→ {rec.recommended_vendor}</span>
                      <span className="text-zinc-500"> — {rec.reasoning}</span>
                    </div>
                  )}
                  {(vendors[p.part_id] || []).length > 1 && (
                    <div className="text-[11px] text-zinc-600 mt-0.5">
                      also: {(vendors[p.part_id] || [])
                        .slice(1, 3)
                        .map((v) => v.vendor_name || v.vendor_type)
                        .join(' · ')}
                    </div>
                  )}
                  {quotingPart === p.part_id ? (
                    <QuoteForm projectId={projectId} partId={p.part_id} onLogged={() => { setQuotingPart(null); load(); }} />
                  ) : (
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        data-testid={`quote-btn-${p.part_id}`}
                        onClick={() => setQuotingPart(p.part_id)}
                        className="text-[10px] text-zinc-500 hover:text-emerald-300 font-mono uppercase"
                      >
                        + log real quote
                      </button>
                      {p.verification !== 'verified' && (
                        <button
                          data-testid={`part-rfq-btn-${p.part_id}`}
                          onClick={() => draftRfq(p.part_id)}
                          className="text-[10px] text-zinc-500 hover:text-amber-300 font-mono uppercase"
                        >
                          + draft RFQ
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {rfqs.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-2">
            RFQ outreach · nothing sends without your approval
          </div>
          <div className="space-y-1.5">
            {rfqs.map((e) => {
              const track = (detail.rfq_tracking || []).find((t) => t.email_id === e.id) || {};
              return (
                <div key={e.id} data-testid={`rfq-${e.id}`} className="flex items-center gap-2 text-[12px] text-zinc-300 flex-wrap">
                  <Mail className="w-3.5 h-3.5 text-amber-400/70 shrink-0" />
                  <span className="truncate max-w-[280px]">{e.subject}</span>
                  <span
                    className={`text-[10px] font-mono uppercase shrink-0 px-1.5 py-0.5 rounded border ${
                      track.status === 'replied'
                        ? 'text-emerald-300 border-emerald-400/40'
                        : track.status === 'sent'
                        ? 'text-blue-300 border-blue-400/30'
                        : track.status === 'queued'
                        ? 'text-amber-300 border-amber-400/30'
                        : track.status === 'expired'
                        ? 'text-red-300/80 border-red-400/30'
                        : 'text-zinc-400 border-zinc-500/30'
                    }`}
                  >
                    {track.status || e.status}
                  </span>
                  {(track.status === 'draft' || !track.status) && (
                    <button
                      data-testid={`rfq-approve-${e.id}`}
                      onClick={async () => {
                        const recipient = window.prompt('Vendor email address to send this RFQ to:');
                        if (!recipient) return;
                        try {
                          const res = await axios.post(
                            `${API_URL}/api/idea/projects/${projectId}/rfqs/${e.id}/approve`,
                            { recipient },
                            auth()
                          );
                          const s = res.data?.send_result?.status;
                          toast.success(s === 'sent' ? 'RFQ sent' : 'RFQ approved — queued (email key pending)');
                          load();
                        } catch (_e) { toast.error('Failed to approve RFQ'); }
                      }}
                      className="text-[10px] text-emerald-300/90 hover:text-emerald-200 font-mono uppercase"
                    >
                      approve + send
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {detail.gtm && (
        <div data-testid="idea-gtm" className="space-y-3 border-t border-white/[0.06] pt-3">
          <div className="text-[11px] uppercase tracking-[0.14em] text-amber-400/80">
            Go-to-Market <span className="text-zinc-600 normal-case tracking-normal">· all market numbers unverified estimates</span>
          </div>
          {detail.gtm.positioning?.positioning_statement && (
            <p className="text-[13px] text-zinc-200 italic" data-testid="gtm-positioning">
              “{detail.gtm.positioning.positioning_statement}”
            </p>
          )}
          {detail.gtm.positioning?.pricing_recommendation?.suggested_price && (
            <div className="text-[12px] text-zinc-300">
              Pricing: <span className="text-emerald-300/90">{detail.gtm.positioning.pricing_recommendation.suggested_price}</span>
              <VerifyBadge verification="unverified" />
            </div>
          )}
          {(detail.gtm.channels || []).length > 0 && (
            <div data-testid="gtm-channels">
              <div className="text-[11px] text-zinc-500 mb-1">Channels · ranked by cost-to-reach</div>
              {detail.gtm.channels.slice(0, 5).map((c, i) => (
                <div key={i} className="text-[12px] text-zinc-400">
                  <span className="font-mono text-zinc-300">{c.rank}.</span> {c.channel}
                  <span className="text-zinc-600"> — {c.expected_cost_to_reach}</span>
                </div>
              ))}
            </div>
          )}
          {(detail.gtm.positioning?.competitors || []).length > 0 && (
            <div data-testid="gtm-competitors">
              <div className="text-[11px] text-zinc-500 mb-1">Competitors</div>
              {detail.gtm.positioning.competitors.slice(0, 4).map((c, i) => (
                <div key={i} className="text-[12px] text-zinc-400">
                  {c.name || c.product_type} — {c.known_price || 'price unknown'}
                  <VerifyBadge verification="unverified" />
                </div>
              ))}
            </div>
          )}
          {(detail.gtm.launch_plan?.materials || []).length > 0 && (
            <div data-testid="gtm-materials">
              <div className="text-[11px] text-zinc-500 mb-1">
                Launch materials · nothing goes public without your approval
              </div>
              {detail.gtm.launch_plan.materials.map((m) => (
                <div key={m.material_id} className="text-[12px] text-zinc-300 flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] uppercase text-zinc-500">{m.type}</span>
                  <span>{m.title}</span>
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                    m.status === 'approved' ? 'text-emerald-300 border-emerald-400/40' : 'text-zinc-400 border-zinc-500/30'
                  }`}>{m.status}</span>
                  {m.status === 'draft' && (
                    <button
                      data-testid={`gtm-approve-${m.material_id}`}
                      onClick={async () => {
                        try {
                          await axios.post(
                            `${API_URL}/api/idea/projects/${projectId}/gtm/materials/${m.material_id}/approve`,
                            {}, auth()
                          );
                          toast.success('Material approved');
                          load();
                        } catch (_e) { toast.error('Failed to approve'); }
                      }}
                      className="text-[10px] text-emerald-300/90 hover:text-emerald-200 font-mono uppercase"
                    >
                      approve
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {detail.version > 1 && <VersionTimeline projectId={projectId} />}

      {(detail.open_questions || []).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-amber-400/80 mb-1.5 flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" /> Open questions for you
          </div>
          <ul className="space-y-1" data-testid="idea-open-questions">
            {detail.open_questions.map((q, i) => (
              <li key={i} className="text-[12px] text-zinc-400">• {q}</li>
            ))}
          </ul>
        </div>
      )}

      {(detail.next_steps || []).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5">Next steps</div>
          <ul className="space-y-1">
            {detail.next_steps.map((s, i) => (
              <li key={i} className="text-[12px] text-zinc-300 flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-400/60 mt-0.5 shrink-0" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function IdeaProjects({ expandId }) {
  const [projects, setProjects] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    if (expandId) {
      setExpanded(expandId);
      setTimeout(() => document.querySelector(`[data-testid="idea-project-${expandId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [expandId]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/idea/projects`, auth());
      setProjects(res.data.projects || []);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const hasActive = projects.some((p) => ['queued', 'running'].includes(p.status));
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(() => { load(); setRefreshKey((k) => k + 1); }, 10000);
    }
    if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [projects, load]);

  if (projects.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="idea-projects-section">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Rocket className="h-5 w-5 text-primary" />
          Idea-to-Reality
        </h2>
        <button
          data-testid="idea-refresh-btn"
          onClick={() => { load(); setRefreshKey((k) => k + 1); }}
          className="text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-3">
        {projects.map((p) => (
          <div key={p.id} data-testid={`idea-project-${p.id}`} className="glass-card p-4">
            <button
              data-testid={`idea-toggle-${p.id}`}
              onClick={() => setExpanded(expanded === p.id ? null : p.id)}
              className="w-full flex items-center justify-between gap-3 text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border shrink-0 ${STATUS_STYLE[p.status] || STATUS_STYLE.queued}`}
                >
                  {p.status === 'running' ? `${p.stage}…` : p.status}
                </span>
                <span className="text-[14px] text-zinc-100 font-medium truncate">{p.title}</span>
                <span className="text-[11px] text-zinc-500 shrink-0">
                  {(p.bom || []).length} parts
                </span>
              </div>
              {expanded === p.id ? (
                <ChevronUp className="w-4 h-4 text-zinc-500 shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
              )}
            </button>
            {expanded === p.id && <ProjectDetail projectId={p.id} refreshKey={refreshKey} />}
          </div>
        ))}
      </div>
    </div>
  );
}
